import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { API, api, fmtErr } from "./api";
import { Modal, labelCls, inputCls, btnPrimary, btnGhost } from "./Modal";
import axios from "axios";

const EMPTY = { name: "", subs: [] };

export const CategoriesTab = () => {
    const [cats, setCats] = useState([]);
    const [editing, setEditing] = useState(null); // null | {id?, name, subs}
    const [busy, setBusy] = useState(false);

    const load = () =>
        axios.get(`${API}/categories`).then((r) => setCats(r.data)).catch(() => toast.error("Could not load categories"));

    useEffect(() => {
        load();
    }, []);

    const openNew = () => setEditing({ ...EMPTY });
    const openEdit = (c) => setEditing({ id: c.id, name: c.name, subs: c.subs.map((s) => ({ ...s })) });

    const save = async () => {
        if (!editing.name.trim()) {
            toast.error("Please type a category name");
            return;
        }
        setBusy(true);
        try {
            const payload = { name: editing.name, subs: editing.subs.filter((s) => s.name.trim()) };
            if (editing.id) {
                await api().put(`/admin/categories/${editing.id}`, payload);
            } else {
                await api().post("/admin/categories", payload);
            }
            toast.success("Saved! Your website is updated.");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete "${c.name}" and its sub-categories? Products already added will keep their old labels.`)) return;
        try {
            await api().delete(`/admin/categories/${c.id}`);
            toast.success("Category deleted");
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        }
    };

    return (
        <div data-testid="categories-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-heading font-bold text-2xl text-navy tracking-tight">Categories & Sub-Categories</h2>
                    <p className="text-sm text-muted mt-1">These become the filter buttons on your website's product catalog.</p>
                </div>
                <button onClick={openNew} className={btnPrimary} data-testid="add-category-button">
                    <Plus size={18} /> Add New Category
                </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4" data-testid="categories-list">
                {cats.map((c) => (
                    <div key={c.id} className="border border-line p-5" data-testid={`category-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <div className="flex items-start justify-between gap-3">
                            <p className="font-heading font-bold text-xl text-navy tracking-tight">{c.name}</p>
                            <div className="flex gap-2 shrink-0">
                                <button onClick={() => openEdit(c)} className="p-2.5 border border-line hover:border-gold hover:text-gold" data-testid={`edit-category-${c.id}`} aria-label="Edit">
                                    <Pencil size={16} />
                                </button>
                                <button onClick={() => remove(c)} className="p-2.5 border border-line hover:border-red-500 hover:text-red-600" data-testid={`delete-category-${c.id}`} aria-label="Delete">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {c.subs.length === 0 && <span className="text-xs text-muted">No sub-categories yet</span>}
                            {c.subs.map((s) => (
                                <span key={s.id} className="text-xs bg-bone border border-line px-3 py-1.5 text-charcoal">{s.name}</span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <Modal title={editing.id ? "Edit Category" : "Add New Category"} onClose={() => setEditing(null)}>
                    <label className={labelCls}>Category Name *</label>
                    <input
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        placeholder="e.g. Office Chairs"
                        className={inputCls}
                        data-testid="category-name-input"
                    />
                    <div className="mt-6">
                        <label className={labelCls}>Sub-Categories (optional)</label>
                        {editing.subs.map((s, i) => (
                            <div key={i} className="flex gap-2 mb-2">
                                <input
                                    value={s.name}
                                    onChange={(e) => {
                                        const subs = [...editing.subs];
                                        subs[i] = { ...subs[i], name: e.target.value };
                                        setEditing({ ...editing, subs });
                                    }}
                                    placeholder="e.g. High Back Chairs"
                                    className={inputCls}
                                    data-testid={`sub-name-input-${i}`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setEditing({ ...editing, subs: editing.subs.filter((_, j) => j !== i) })}
                                    className="p-3 border border-line hover:border-red-500 hover:text-red-600 shrink-0"
                                    data-testid={`remove-sub-${i}`}
                                    aria-label="Remove"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() => setEditing({ ...editing, subs: [...editing.subs, { id: crypto.randomUUID(), name: "" }] })}
                            className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-navy border border-dashed border-line px-4 py-2.5 hover:border-gold hover:text-gold"
                            data-testid="add-sub-button"
                        >
                            <Plus size={15} /> Add a sub-category
                        </button>
                    </div>
                    <div className="flex gap-3 mt-8">
                        <button onClick={save} disabled={busy} className={btnPrimary} data-testid="save-category-button">
                            {busy ? "Saving..." : "Save Category"}
                        </button>
                        <button onClick={() => setEditing(null)} className={btnGhost} data-testid="cancel-category-button">Cancel</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};
